import React from 'react';
import ReactDOM from 'react-dom';
import cx from 'classnames';

import Box from 'components/Box.jsx';
import Glyph from 'components/Glyph.jsx';
import { getPropClassesFromInstance } from 'lib/getPropClasses';
import Image from 'components/Image.jsx';
import Link from 'components/Link.jsx';
import ClickEventTrigger from 'components/ClickEventTrigger.jsx';

import './styles/DialogContent.scss';

class DialogContent extends React.Component {
  static propTypes = {
    allowOverflow: React.PropTypes.bool,
    onRequestHide: React.PropTypes.func,
    glyph: React.PropTypes.string,
    glyphColor: React.PropTypes.oneOf(['inherit', 'lava', 'shamrock', 'violet']),
    image: React.PropTypes.string,
    headerImageComponent: React.PropTypes.node,
    imageSize: React.PropTypes.oneOf(['default', 'medium', 'large']),
    title: React.PropTypes.node,
    subtext: React.PropTypes.node,
    size: React.PropTypes.oneOf(['xlarge', 'large', 'medium', 'small', 'xsmall']),
    position: React.PropTypes.oneOf(['center', 'right']),
    headerTextAlign: React.PropTypes.oneOf(['center', 'left', 'right']),
    hideCloseButton: React.PropTypes.bool,
    hideOnBlur: React.PropTypes.bool,
    hidePreviousButton: React.PropTypes.bool,
    onRequestPrevious: React.PropTypes.func,
    onMaskHide: React.PropTypes.func,
  };

  static propClasses = {
    size: {
      xlarge: 'DialogContent-xlarge',
      large: 'DialogContent-large',
      medium: 'DialogContent-medium',
      small: 'DialogContent-small',
      xsmall: 'DialogContent-xsmall',
    },
    position: {
      center: 'DialogContent-center',
      right: 'DialogContent-right',
    },
    headerTextAlign: {
      center: 'DialogContent-headerTextCenter',
      left: 'DialogContent-headerTextLeft',
      right: 'DialogContent-headerTextRight',
    },
    allowOverflow: 'DialogContent-overflow',
  };

  static defaultProps = {
    allowOverflow: false,
    glyphColor: 'inherit',
    imageSize: 'default',
    size: 'medium',
    position: 'center',
    hideCloseButton: true,
    hideOnBlur: false,
    hidePreviousButton: true,
    onRequestPrevious: () => {},
    onMaskHide: null,
    headerTextAlign: 'center',
  };

  state = {
    paddingTop: null,
  };

  componentDidMount() {
    window.addEventListener('resize', this.resetPosition, false);
    // FIXME: This is to allow ScrollableArea to set a height before we try to center the dialog
    this.timeout = setTimeout(() => this.resetPosition(), 0);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resetPosition, false);
    window.clearTimeout(this.timeout);
  }

  closeDialog = (event) => {
    const box = ReactDOM.findDOMNode(this.refs.box);
    // in IE only, box is somehow sometimes null
    // https://my.trackjs.com/messages/filtered?hash=c677c309de472679
    if (box && box.contains(event.target)) {
      return;
    }

    event.stopPropagation();
    if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
  };

  handleClick = (event) => {
    const box = ReactDOM.findDOMNode(this.refs.box);
    if (!this.props.hideOnBlur || box.contains(event.target)) {
      return;
    }

    if (this.props.onMaskHide) {
      this.props.onMaskHide();
    } else if (this.props.onRequestHide) {
      this.props.onRequestHide();
    }
  };

  resetPosition = () => {
    const root = ReactDOM.findDOMNode(this);
    const container = ReactDOM.findDOMNode(this.refs.container);
    let paddingTop = 0;

    if (this.props.position === 'center') {
      paddingTop = (root.offsetHeight - container.offsetHeight) / 2;
    }

    this.setState({ paddingTop });
  };

  renderCloseButton = () => {
    if (this.props.hideCloseButton) {
      return null;
    }

    return (
      <Link
        className="DialogContent-closeButton"
        onClick={this.closeDialog}
        eventElementName="DialogContent-closeButton">
        <Glyph name="close" size="large" solo />
      </Link>
    );
  };

  renderGlyph = () => {
    const { glyph } = this.props;
    if (!glyph) {
      return null;
    }

    return (
      <Glyph
        className="DialogContent-glyph"
        name={glyph}
        color={this.props.glyphColor}
        size="xxxlarge"
      />
    );
  };

  renderHeader = () => {
    const previousButton = this.renderPreviousButton();
    const glyph = this.renderGlyph();
    const image = this.renderImage();
    const title = this.renderTitle();
    const subtext = this.renderSubtext();

    if (!(previousButton || glyph || image || title || subtext)) {
      return null;
    }

    return (
      <header className="DialogContent-header">
        {previousButton}
        {glyph}
        {image}
        {title}
        {subtext}
      </header>
    );
  };

  renderImage = () => {
    const { image, headerImageComponent } = this.props;
    if (image) {
      const className = `DialogContent-image-${this.props.imageSize}`;
      return <Image className={className} src={image} />;
    } else if (headerImageComponent) {
      return headerImageComponent;
    } else {
      return null;
    }
  };

  renderPreviousButton = () => {
    if (this.props.hidePreviousButton) {
      return null;
    }

    return (
      <Link
        className="DialogContent-previousButton"
        onClick={this.props.onRequestPrevious}
        eventElementName="DialogContent-previousButton">
        <Glyph name="arrow-left" color="brand" size="xlarge" solo />
      </Link>
    );
  };

  renderSubtext = () => {
    const { subtext } = this.props;
    if (!subtext) {
      return null;
    }

    return <div className="DialogContent-subtext">{subtext}</div>;
  };

  renderTitle = () => {
    const { title } = this.props;
    if (!title) {
      return null;
    }

    return <h2 className="DialogContent-title">{title}</h2>;
  };

  render() {
    return (
      <div
        className={cx('DialogContent', getPropClassesFromInstance(this), this.props.className)}
        style={
          this.state.paddingTop ?
          { } :
          { opacity: 0.01 }
        }>
        <ClickEventTrigger eventElementName="DialogContent-closeButton">
          <div
            className="DialogContent-wrap"
            onClick={this.handleClick}
            style={{
              paddingTop: this.state.paddingTop,
            }}>
            <div ref="container" className="DialogContent-container">
              <Box ref="box" className="DialogContent-box">
                {this.renderHeader()}
                {this.props.children}
              </Box>
              {this.renderCloseButton()}
            </div>
          </div>
        </ClickEventTrigger>
      </div>
    );
  }
}

export default DialogContent;



// WEBPACK FOOTER //
// ./src/components/DialogContent.jsx